<template>
  <transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
      @click.self="$emit('update:modelValue', false)"
    >
      <div
        class="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative"
      >
        <!-- Close Button -->
        <button
          @click="$emit('update:modelValue', false)"
          class="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition z-50"
        >
          ✕
        </button>

        <!-- EVERYTHING BELOW MUST BE INSIDE ONE WRAPPER -->
        <div class="p-6 sm:p-8">
          <!-- Header -->
          <div class="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h3 class="text-xl sm:text-2xl font-bold text-gray-900">
                {{ userName || "User" }}'s Leave Balance
              </h3>
              <p class="text-sm text-gray-500">
                Track and manage leave balance details
              </p>
            </div>

            <!-- ✅ Year Filter -->
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-600"> Year: </label>
              <input
                v-model.number="selectedYear"
                type="number"
                min="2000"
                :max="new Date().getFullYear() + 1"
                class="w-28 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>
          <!-- Loading State -->
          <div
            v-if="isLoading"
            class="mb-8 rounded-3xl p-6 bg-white border border-indigo-100 flex items-center justify-center h-[300px]"
          >
            <div class="flex flex-col items-center gap-3">
              <div
                class="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent"
              ></div>
              <p class="text-sm text-gray-500">Loading leave data...</p>
            </div>
          </div>
          <!-- ===== NEW: MONTHLY LEAVE USAGE CHART ===== -->
          <div
            v-if="leaveBalance && leaveBalance.balances?.length"
            class="mb-8 rounded-3xl p-6 bg-gradient-to-br from-white to-indigo-50/40 border border-indigo-100"
          >
            <h4
              class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"
            >
              <BarChart3 class="w-5 h-5 text-indigo-600" />
              Monthly Leave Usage
            </h4>

            <!-- Chart Container -->
            <div
              v-if="monthlyChartData.length > 0"
              class="relative h-[500px] w-full"
            >
              <canvas
                ref="chartCanvas"
                class="w-full h-full"
              ></canvas>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="!isLoading"
              class="text-center py-8"
            >
              <p class="text-sm text-gray-500">
                No leave data available for {{ selectedYear }}
              </p>
            </div>
          </div>
          <!-- ===== END: MONTHLY LEAVE USAGE CHART ===== -->

          <!-- IF BALANCES EXIST -->
          <div
            v-if="!isLoading && leaveBalance && leaveBalance.balances?.length"
            class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            <div
              v-for="(item, index) in leaveBalance.balances"
              :key="index"
              class="relative rounded-3xl p-6 bg-gradient-to-br from-white to-indigo-50/40 border border-indigo-100 group overflow-hidden"
            >
              <!-- Accent Glow -->
              <div
                class="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-indigo-100/40 to-blue-100/40 transition duration-500"
              ></div>

              <!-- Top Row -->
              <div class="relative flex justify-between items-start mb-6">
                <div>
                  <p class="text-sm text-gray-500 mb-1">Leave Type</p>
                  <h4 class="text-xl font-bold text-gray-900">
                    {{ item.type }}
                  </h4>
                </div>

                <div class="flex items-center gap-3">
                  <!-- Remaining Big Badge -->
                  <div
                    class="flex flex-col items-center justify-center bg-white rounded-2xl px-4 py-2 shadow-sm border"
                  >
                    <span
                      class="text-2xl font-bold"
                      :class="
                        item.remaining === 0
                          ? 'text-red-600'
                          : 'text-indigo-600'
                      "
                    >
                      {{ item.remaining }}
                    </span>
                    <span class="text-xs text-gray-500">Left</span>
                  </div>

                  <!-- 3 Dot Menu -->
                  <div
                    v-if="userRole === 'admin'"
                    class="relative"
                  >
                    <button
                      @click.stop="toggleMenu(index)"
                      class="p-2 rounded-full hover:bg-gray-100 transition"
                    >
                      <MoreVertical class="w-5 h-5 text-gray-600" />
                    </button>

                    <!-- Dropdown -->
                    <div
                      v-if="activeMenu === index"
                      class="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                    >
                      <button
                        @click="
                          openEditModal(item);
                          closeMenu();
                        "
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-indigo-600 hover:bg-indigo-50 transition"
                      >
                        <Edit3 class="w-4 h-4" />
                        Update
                      </button>

                      <button
                        @click="
                          requestDeleteLeaveType(item);
                          closeMenu();
                        "
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition border-t border-gray-100"
                      >
                        <Trash2 class="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Usage Stats -->
              <div class="relative mb-4">
                <div class="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Used: {{ item.used }}</span>
                  <span>Total: {{ item.total }}</span>
                </div>

                <!-- Premium Progress Bar -->
                <div
                  class="w-full h-3 bg-gray-200 rounded-full overflow-hidden"
                >
                  <div
                    class="h-3 rounded-full transition-all duration-700"
                    :class="
                      item.remaining === 0
                        ? 'bg-red-500'
                        : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500'
                    "
                    :style="{
                      width:
                        item.total > 0
                          ? (item.used / item.total) * 100 + '%'
                          : '0%',
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- IF NO POLICY -->
          <div
            v-else-if="!isLoading"
            class="text-center py-12"
          >
            <div
              class="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <Calendar class="w-8 h-8 text-indigo-600" />
            </div>

            <h4 class="text-lg font-semibold text-gray-800 mb-2">
              No Leave Policy Configured
            </h4>

            <p class="text-sm text-gray-500 mb-6">
              Create a leave policy to start tracking leave balances.
            </p>

            <router-link
              v-if="userRole === 'admin'"
              :to="{
                path: '/schoolDetails',
                query: { openPolicy: 'true' },
              }"
              class="inline-block px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-semibold text-sm text-center"
            >
              Create Leave Policy
            </router-link>
          </div>
        </div>

        <!-- Edit Total Modal -->
        <div
          v-if="showEditModal && editingBalance"
          class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <div
            class="bg-white rounded-3xl w-full max-w-md shadow-2xl border border-gray-100 overflow-hidden"
          >
            <!-- Header -->
            <div class="px-6 py-5 border-b bg-indigo-50/60">
              <h2
                class="flex items-center gap-3 text-xl font-bold text-gray-900"
              >
                <Calendar class="w-5 h-5 text-indigo-600" />
                Update Leave
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                {{ userName }} • {{ editingBalance.type }}
              </p>
            </div>

            <!-- Body -->
            <div class="p-6 space-y-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  New Total Leave
                </label>
                <input
                  v-model.number="editingBalance.newTotal"
                  type="number"
                  min="0"
                  class="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg transition"
                />
                <p class="text-xs text-gray-500 mt-2">
                  Current: {{ editingBalance.total }} • Used:
                  {{ editingBalance.used }}
                </p>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                @click="showEditModal = false"
                class="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition"
              >
                Cancel
              </button>

              <button
                @click="submitEditBalance"
                class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md transition"
              >
                Update
              </button>
            </div>
          </div>
        </div>
        <ConfirmModal
          :show="showConfirmModal"
          :title="confirmConfig.title"
          :description="confirmConfig.description"
          :buttonText="confirmConfig.buttonText"
          :isDestructive="confirmConfig.isDestructive"
          :isLoading="confirmLoading"
          @cancel="showConfirmModal = false"
          @confirm="confirmConfig.action"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
  import {
    ref,
    watch,
    computed,
    reactive,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from "vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import ConfirmModal from "@/components/Students/ConfirmationPopupModal.vue";
  import {
    Calendar,
    MoreVertical,
    Edit3,
    Trash2,
    BarChart3,
  } from "lucide-vue-next";
  import Chart from "chart.js/auto";
  import store from "@/store";

  const selectedYear = ref(new Date().getFullYear());
  let chartInstance = null;
  const leaveBalance = computed(() => {
    const data =
      store.state.userLeaveBalances?.[props.userId]?.[selectedYear.value];

    return isLoading.value ? null : data ?? null;
  });

  /* ============================= */
  /* PROPS & EMITS */
  /* ============================= */

  const props = defineProps({
    modelValue: Boolean,
    userId: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: "",
    },
  });

  defineEmits(["update:modelValue"]);

  const { showToast } = useGlobalToast();

  /* ============================= */
  /* UI STATE */
  /* ============================= */

  const isLoading = ref(false);
  const showEditModal = ref(false);
  const showConfirmModal = ref(false);
  const confirmLoading = ref(false);
  const activeMenu = ref(null);
  const chartCanvas = ref(null);

  /* ============================= */
  /* CONFIRM CONFIG */
  /* ============================= */

  const confirmConfig = reactive({
    title: "",
    description: "",
    buttonText: "Confirm",
    isDestructive: false,
    action: null,
  });

  /* ============================= */
  /* MENU HANDLING */
  /* ============================= */

  const toggleMenu = (index) => {
    activeMenu.value = activeMenu.value === index ? null : index;
  };

  const closeMenu = () => {
    activeMenu.value = null;
  };

  /* ============================= */
  /* EDIT BALANCE STATE (Optimized) */
  /* ============================= */

  const editingBalance = ref(null);

  const openEditModal = (item) => {
    editingBalance.value = {
      ...item,
      newTotal: item.total,
    };
    showEditModal.value = true;
  };

  /* ============================= */
  /* FETCH BALANCE */
  /* ============================= */

  const fetchBalance = async () => {
    if (!props.userId) return;
    isLoading.value = true;
    try {
      await store.dispatch("fetchUserLeaveBalance", {
        userId: props.userId,
        year: selectedYear.value,
      });

      // Update chart after data is fetched
      await nextTick();
      updateChart();
    } catch (err) {
      console.error("❌ Failed to fetch leave balance", err);
    } finally {
      isLoading.value = false;
    }
  };

  watch(selectedYear, async () => {
    if (props.modelValue && props.userId) {
      await fetchBalance();
    }
  });

  /* ============================= */
  /* SINGLE OPTIMIZED WATCHER */
  /* ============================= */

  watch(
    [() => props.modelValue, () => props.userId],
    async ([isOpen, userId]) => {
      if (isOpen && userId) {
        await fetchBalance();
      }
    },
    { immediate: true },
  );

  /* ============================= */
  /* UPDATE BALANCE */
  /* ============================= */

  const submitEditBalance = async () => {
    if (!editingBalance.value) return;

    if (editingBalance.value.newTotal < editingBalance.value.used) {
      showToast("error", "New total cannot be less than used leaves!");
      return;
    }

    try {
      isLoading.value = true;

      await store.dispatch("updateLeaveBalance", {
        leaveType: editingBalance.value.type,
        total: editingBalance.value.newTotal,
        userId: props.userId,
      });

      await fetchBalance();

      showEditModal.value = false;
      editingBalance.value = null;

      showToast("success", "Leave balance updated successfully!");
    } catch (err) {
      console.error("Failed to update balance", err);
      showToast("error", "Failed to update leave balance");
    } finally {
      isLoading.value = false;
    }
  };

  /* ============================= */
  /* DELETE LEAVE TYPE */
  /* ============================= */

  const requestDeleteLeaveType = (item) => {
    if (item.used > 0) {
      showToast(
        "error",
        `Cannot delete ${item.type} because ${item.used} leave(s) have already been used.`,
      );
      return;
    }

    confirmConfig.title = "Delete Leave Type";
    confirmConfig.description = `Are you sure you want to permanently delete "${item.type}"? This action cannot be undone.`;
    confirmConfig.buttonText = "Delete";
    confirmConfig.isDestructive = true;

    confirmConfig.action = async () => {
      confirmLoading.value = true;

      try {
        await store.dispatch("deleteLeaveType", item.type);

        await fetchBalance();

        showToast("success", "Leave type deleted successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to delete leave type.",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  /* ============================= */
  /* ===== NEW: CHART LOGIC ===== */
  /* ============================= */

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Color palette for different leave types (Indigo theme)
  const colorPalette = {
    Casual: "#6366f1",
    Personal: "#8b5cf6",
    Sick: "#ec4899",
    Earned: "#f59e0b",
    Unpaid: "#ef4444",
    Maternity: "#f97316",
  };

  const getColorForLeaveType = (leaveType, index) => {
    return colorPalette[leaveType] || `hsl(${index * 60}, 70%, 50%)`;
  };

  // ===== NEW: Computed property to transform API data into chart format =====
  const monthlyChartData = computed(() => {
    if (!leaveBalance.value?.balances?.length) return [];

    // This assumes your API returns monthly data structure like:
    // { month: "Jan", type: "Casual", count: 2 }
    // If your API structure is different, transform it accordingly here
    return leaveBalance.value.monthlyData || [];
  });

  const initializeChart = async () => {
    await nextTick();

    if (!chartCanvas.value) return;

    // Destroy previous chart instance
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }

    if (monthlyChartData.value.length === 0) return;

    // Collect unique leave types
    const leaveTypeSet = new Set();
    monthlyChartData.value.forEach((item) => {
      leaveTypeSet.add(item.type);
    });

    // Build stacked datasets
    const datasets = Array.from(leaveTypeSet).map((leaveType, index) => {
      const data = months.map((month) => {
        const found = monthlyChartData.value.find(
          (item) => item.month === month && item.type === leaveType,
        );
        return found ? found.count : 0;
      });

      const color = getColorForLeaveType(leaveType, index);

      return {
        label: leaveType,
        data,
        backgroundColor: color,
        borderRadius: 6,
        borderSkipped: false,
        stack: "total", // 👈 Important for stacking
        maxBarThickness: 40,
      };
    });

    chartInstance = new Chart(chartCanvas.value, {
      type: "bar",
      data: {
        labels: months,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: window.devicePixelRatio || 2,

        animation: {
          duration: 900,
          easing: "easeOutQuart",
        },

        interaction: {
          mode: "index",
          intersect: false,
        },

        plugins: {
          legend: {
            position: "top",
            labels: {
              font: {
                size: 12,
                weight: "600",
              },
              color: "#6b7280",
              padding: 15,
              usePointStyle: true,
            },
          },

          tooltip: {
            backgroundColor: "rgba(31, 41, 55, 0.95)",
            padding: 12,
            cornerRadius: 10,
            callbacks: {
              title: (context) => context[0].label,
              label: (context) =>
                `${context.dataset.label}: ${context.parsed.y} leaves`,
              footer: (tooltipItems) => {
                const total = tooltipItems.reduce(
                  (sum, item) => sum + item.parsed.y,
                  0,
                );
                return `Total: ${total} leaves`;
              },
            },
          },
        },

        scales: {
          x: {
            stacked: true,
            grid: {
              color: "rgba(99, 102, 241, 0.08)", // light vertical lines
              drawBorder: false,
            },
            ticks: {
              font: { size: 13, weight: "600" },
              color: "#374151",
            },
          },

          y: {
            stacked: true, // 👈 Enable stacking
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              font: { size: 13, weight: "600" },
              color: "#374151",
            },
            grid: {
              color: "rgba(99, 102, 241, 0.18)", // stronger visibility
              lineWidth: 1.2,
              drawBorder: false,
              drawTicks: false,
            },
            border: {
              display: false,
            },
          },
        },
      },
    });
  };

  const updateChart = async () => {
    await initializeChart();
  };

  // Watch for changes to monthlyChartData and update chart
  watch(
    monthlyChartData,
    async () => {
      await updateChart();
    },
    { deep: true },
  );

  /* ============================= */
  /* ===== END: CHART LOGIC ===== */
  /* ============================= */

  /* ============================= */
  /* COMPUTED */
  /* ============================= */

  const userRole = computed(() => store.state.userRole);

  const modalRef = ref(null);

  const handleClickOutside = (event) => {
    if (!modalRef.value?.contains(event.target)) {
      activeMenu.value = null;
    }
  };

  onMounted(() => {
    document.addEventListener("click", handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
    if (chartInstance) {
      chartInstance.destroy();
    }
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
    will-change: opacity, transform;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.99);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
</style>
